import { jsonify, sanitize } from "../TapiocaUtils";
export default class BridgeClient {
  constructor(raw) {
    this.raw = raw;
  }
  /**
   * Activates the automations with the uids passed by parameter.
   * Automations can be deactivated on the source and/or target side, if an automation is
   * deactivated (either on source or target side) events that would match it would be
   * ignored (as if it didn't exist).  And so, depending on the following non-exclusive
   * conditions an automation can be "source and/or target re-activated":
   * * If the requesting user is an admin on the source project or if it is the user that
   * created the automation in the first place, then it will be "source activated".
   * * If the requesting user is an admin on the target project, then it will be "target
   * activated".
   * If you request to activate an automation that's already active, nothing will happen.
   * Note that this endpoint will collect the source and target projects of all the given
   * automations and then only source/target-activate them if the user is admin of all the
   * source or all the target projects, (or source-activate them if the user is the
   * requesting user is the sender_user_oid of all the automations).
   * In case of doubt, call this endpoint on a set of automations sharing the same
   * (source_project_uid, target_project_uid) pair.
   */
  activateAutomationsBatch(body) {
    const url = "/v1/automations/activate:batch";
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * Soft-Delete the Automations identified by the uids passed by parameter.
   * Deleting an automation does not result in the deletion of any asset, its effect is
   * equivalent to the automations themselves not existing, that is deleted automations
   * will not get triggered when a matching even happens.
   * In order to delete an automation, the user has to be an admin on at least the source
   * or target projects.
   */
  batchDelete(body) {
    const url = "/v1/automations:batch-delete";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint performs cleanup related to a completed file package.
   * Should ONLY be called by PADD when the clone associated to the transaction is
   * completed.  The payload used here corresponds to what the `clone_completion_callback`
   * task sends when clones complete.
   * Note that endpoint will only be used when the clone is created with a callback url,
   * and this only happens when the PL_BRIDGE_TO_PADD_API FF is enabled (it will always be
   * enabled once bridge is separated from PADD).
   */
  completeFilePackage(body) {
    const url = "/v1/file-package-completion";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint creates a bridge based on a pending bridge request.
   */
  createBridge(body) {
    const url = "/v1/bridges";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Deactivates the automations with the uids passed by parameter.
   * If the requesting user is the sender_user_oid of all the automations from the given
   * source to target project, or if the user is an admin of the source and/or target
   * projects, then the all the automations from the source to the target project passed
   * by parameter will be source and target-deactivated.
   */
  deactivateAutomationsBatch(body) {
    const url = "/v1/automations/deactivate:batch";
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * This endpoint deletes an active bridge.  A deleted bridge will be left as INACTIVE.
   * Deleting a bridge will also delete the automations between both projects (in both
   * directions).
   */
  deleteBridge(bridgeId, params) {
    const url = {
      url: `/v1/bridges/${bridgeId}`,
      params: sanitize(params || {}),
    };
    return this.raw.delete(url);
  }
  /**
   * Given `source_project_uid` and `target_project_uid`, this endpoint soft-deletes the
   * automations from the specified source to the target projects.  A comma separated list
   * of `asset_types` can be specified if we want to delete only the automations with those
   * asset types.
   * User has to be an admin of at least the source or target projects specified.
   */
  deleteProjectAutomations(sourceProjectUid, targetProjectUid, params) {
    const url = {
      url: `/v1/automations/${sourceProjectUid}/${targetProjectUid}`,
      params: sanitize(params || {}, { asset_types: "csv" }),
    };
    return this.raw.delete(url);
  }
  /**
   * This endpoint is the last step of a S2R file package, accepting one or more sheets
   * from it into a specific target project.  The specified assets have to be a non-empty
   * non-strict subset of the assets originally included in the file package.
   * The accepting user has to be an admin of the target project.
   * Here we return a transactionId so that it can be used to specify more assets to accept
   * into the same transaction.  That said, a file package can be accepted more than once.
   * Accepting a file package (when hasMoreAssets: false) results in a clone copying those
   * assets and their dependencies into the target project.
   */
  filePackageAccept(body) {
    const url = "/v1/transaction";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint can be used to add more assets to a pending transaction created with the
   * `accept_file_package` endpoint (POST /v1/transaction).  A transaction is pending if
   * it was created with hasMoreAssets set to false and if any further calls to this
   * endpoint (if any) for that same transaction also had hasMoreAssets=false.
   * Once `hasMoreData` is set to True, the copy of the selected assets will begin.
   */
  filePackageAcceptAddAssets(transactionId, body) {
    const url = `/v1/transaction/${transactionId}`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint will add assets to a pending file package.  A file package will be sent
   * once hasMoreData is set to false.
   */
  filePackageAddAssets(filePackageId, body) {
    const url = `/v1/file-package/${filePackageId}`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint will create a file package and return its identifier.  File packages
   * can be sent to one or more project or to one or more emails.  Additionally, 'to_self'
   * file packages can enable automations.
   * If you need to specify many assets (more than the limit) you can do pagination by
   * setting `hasMoreData` to true and calling POST /v1/file-package/<filePackageId> to
   * add more assets.
   */
  filePackageCreate(body) {
    const url = "/v1/file-package";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint fetches information about a bridge request.  There is no authorization
   * performed here, as we need to access the Bridge Request in the context of an unlogged
   * user receiving the bridge request email.
   */
  getBridgeRequest(bridgeRequestId) {
    const url = `/v1/bridge-requests/${bridgeRequestId}`;
    return this.raw.get(url);
  }
  /**
   * Gets a list of neighboring projects for the source project of a bridge request.
   * We use this endpoint for filtering out (in the frontend) the projects that are already
   * bridged with this request sending project.
   * Since we don't check for authentication (this endpoint has to be queried in the
   * context of an unlogged user receiving a bridge request email), we only return a list
   * of the associated project uids.
   */
  getBridgeRequestNeighbors(bridgeRequestId, params) {
    const url = {
      url: `/v1/bridge-requests/${bridgeRequestId}/sender-neighbors`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint fetches bridge requests for a given project (requests that have a given
   * initial_project_uid).  The requesting user has to be a valid user of such project.
   */
  getBridgeRequests(params) {
    const url = {
      url: "/v1/bridge-requests",
      params: sanitize(params || {}, { "filter[status]": "csv" }),
    };
    return this.raw.get(url);
  }
  getFiltersV2(params) {
    const url = {
      url: "/v2/get-filters",
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint fetches bridges for a given project.  Bridged projects form a global
   * acyclic undirected graph, with this endpoint you can obtain the direct neighbors of a
   * given project uid.
   */
  getProjectBridges(params) {
    const url = {
      url: "/v1/bridges",
      params: sanitize(params || {}, {
        fields: "csv",
        "filter[status]": "csv",
      }),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint fetches bridges for a given project.  Bridged projects form a global
   * acyclic undirected graph, with this endpoint you can obtain the direct neighbors of a
   * given project uid.
   */
  getProjectBridgesV2(params) {
    const url = {
      url: "/v2/bridges",
      params: sanitize(params || {}, {
        fields: "csv",
        "filter[status]": "csv",
      }),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint fetches a single sent transaction based on its unique identifier.
   * The requesting user must be a member of its source project.
   */
  getReceivedTransaction(transactionId) {
    const url = `/v1/transaction/received_transaction/${transactionId}`;
    return this.raw.get(url);
  }
  /**
   * This endpoint can be used to list the assets of a received transaction.  Note that
   * the dependencies of the assets will not be listed (unless they were selected when
   * the received transaction was created (at acceptance on 'to_receiver' or at POST
   * /file-package on 'to_self'))
   */
  getReceivedTransactionAssets(transactionId, params) {
    const url = {
      url: `/v1/received_transaction/${transactionId}/assets`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * DEPRECATED:
   * This endpoint is being deprecated in favor of `get_received_transactions_v2`:
   * `GET /v2/received-transactions`
   * This endpoint returns the list of incoming transactions for the given project.
   * The requesting user must be a member of the project (or account admin).
   */
  getReceivedTransactions(params) {
    const url = {
      url: "/v1/received_transactions",
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint returns the list of incoming transactions for the given project.
   * The requesting user must be a member of the project (or account admin).
   * The main difference in v2 (in comparison to v1) is that Forge API Guidelines are
   * now being followed.
   */
  getReceivedTransactionsV2(params) {
    const url = {
      url: "/v2/received-transactions",
      params: sanitize(params || {}, {
        "filter[addedToProjectBy]": "csv",
        "filter[assetTypes]": "csv",
        "filter[receivedFromProjects]": "csv",
        "filter[receivedFromUsers]": "csv",
        "filter[reviewedBy]": "csv",
        "filter[status]": "csv",
      }),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint fetches a single sent (outgoing) transaction based on its unique
   * identifier.
   * The requesting user must be a member of its source project.
   */
  getSentTransaction(transactionId) {
    const url = `/v1/transaction/sent_transaction/${transactionId}`;
    return this.raw.get(url);
  }
  /**
   * DEPRECATED:
   * This endpoint is being deprecated in favor of `get_sent_transactions_v2`:
   * `GET /v2/sent-transactions`
   * This endpoint returns the list of outgoing transactions for the given project.
   * The requesting user must be a member of the project (or account admin).
   */
  getSentTransactions(params) {
    const url = {
      url: "/v1/sent_transactions",
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint can be used to list the assets of a sent transaction.  Note that
   * the dependencies of the assets will not be listed (unless they were selected when
   * the received transaction was created).
   */
  getSentTransactionsAssets(transactionId, params) {
    const url = {
      url: `/v1/sent_transaction/${transactionId}/assets`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint returns the list of outgoing transactions for the given project.
   * The requesting user must be a member of the project (or account admin).
   * The main difference in v2 (in comparison to v1) is that Forge API Guidelines are
   * now being followed.
   */
  getSentTransactionsV2(params) {
    const url = {
      url: "/v2/sent-transactions",
      params: sanitize(params || {}, {
        "filter[assetTypes]": "csv",
        "filter[sentBy]": "csv",
        "filter[sentToProjects]": "csv",
        "filter[sentToUsers]": "csv",
        "filter[status]": "csv",
      }),
    };
    return this.raw.get(url);
  }
  getTransactionReviewers(transactionId, params) {
    const url = {
      url: `/v1/received-transaction/${transactionId}/reviewers`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Method: Get, query parameters.
   * Use this endpoint to obtain a list of the currently created automations.  When calling
   * this endpoint you must at least specify two of the following: `source_project_uid`,
   * `target_project_uid` and (`asset_type` or `asset_types`), that is you can use:
   * * `source_project_uid` and `asset_type`:  To obtain the list of automations
   * originating from a given project that have a specific asset type.
   * * `target_project_uid` and `asset_type`:  To obtain the list of automations pointing
   * to the given project with a specific asset type.
   * * `source_project_uid` and `target_project_uid`:  For listing the automations
   * originate and end in the given projects.
   * You can also specify all three of them.
   * * `asset_types`: it's an optional asset type list that will replace asset_type field, meanwhile
   * asset_type exists, it will have a priority over asset_types in order to preserve backwards compatibility
   * * `fields`: optional field that allows asking for specific fields in the response.
   * * Other parameters for filtering usage:
   * * source_history_ids: comma separated list of ids
   * * target_history_ids: comma separated list of ids
   * * source_collection_ids: comma separated list of ids
   * * target_collection_ids: comma separated list of ids
   * * source_folder_ids: comma separated list of ids
   * * target_folder_ids: comma separated list of ids
   * For authentication, we validate that the requesting user is a valid user of at least
   * the source or target project.
   * This endpoint does not return the total number of results.
   */
  queryAutomationsGet(params) {
    const url = {
      url: "/v1/automations",
      params: sanitize(params || {}, {
        asset_types: "csv",
        fields: "csv",
        owner: "csv",
        source_collection_ids: "csv",
        source_folder_ids: "csv",
        source_history_ids: "csv",
        target_collection_ids: "csv",
        target_folder_ids: "csv",
        target_history_ids: "csv",
      }),
    };
    return this.raw.get(url);
  }
  /**
   * Method: Post, body parameters.
   * Use this endpoint to obtain a list of the currently created automations.  When calling
   * this endpoint you must at least specify two of the following: `source_project_uid`,
   * `target_project_uid` and `asset_types`, that is you can use:
   * * `source_project_uid` and `asset_types`:  To obtain the list of automations
   * originating from a given project that have a specific asset type.
   * * `target_project_uid` and `asset_types`:  To obtain the list of automations pointing
   * to the given project with a specific asset type.
   * * `source_project_uid` and `target_project_uid`:  For listing the automations
   * originate and end in the given projects.
   * You can also specify all three of them.
   * * `asset_types`: it's an optional asset type list that will replace asset_type field, meanwhile
   * asset_type exists, it will have a priority over asset_types in order to preserve backwards compatibility
   * * `fields`: optional field that allows asking for specific fields in the response.
   * * Other parameters for filtering usage:
   * * source_history_ids: comma separated list of uuids
   * * target_history_ids: comma separated list of uuids
   * * source_collection_ids: comma separated list of uuids
   * * target_collection_ids: comma separated list of uuids
   * * source_folder_ids: comma separated list of uuids
   * * target_folder_ids: comma separated list of uuids
   * For authentication, we validate that the requesting user is a valid user of at least
   * the source or target project.
   * This endpoint does not return the total number of results.
   */
  queryAutomationsPost(body) {
    const url = "/v1/automations:batch-get";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint creates a bridge request sending an email in the process, and returning
   * the newly created bridge.
   * The requesting user has to be a valid user of the initial projectId.
   */
  requestBridge(body) {
    const url = "/v1/bridge-requests";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint resends a pending bridge request.
   * A bridge request can only be resent by the original sender or a project admin.
   */
  resendBridgeRequest(bridgeRequestId) {
    const url = `/v1/bridge-requests/${bridgeRequestId}/resend`;
    return this.raw.get(url);
  }
  reviewReceivedTransaction(transactionId, body) {
    const url = `/v1/received-transaction/${transactionId}/review`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint is called by PADD when a snapshot completes. Bridge initiates
   * a snapshot when a 'for review' file package is created. When PADD responds, this
   * endpoint is responsible for moving forward with the file package depending on the
   * status of the snapshot.
   * If the snapshot was successful, we update the transactions and notify the reviewers.
   * If the snapshot fails, we mark the transactions as failed and notify the sender.
   */
  snapshotCompletion(transactionId, body) {
    const url = `/v1/received_transaction/${transactionId}/snapshot-completion`;
    return this.raw.post(url, jsonify(body));
  }
  systemInfo() {
    const url = "/v1/system/info";
    return this.raw.get(url);
  }
  /**
   * This endpoint withdraws a bridge request.
   * Withdrawn bridge requests cannot be accepted, yet they still can be queried.
   * The requesting user has to be a member of the initial project of the bridge request.
   */
  withdrawBridgeRequest(bridgeRequestId) {
    const url = `/v1/bridge-requests/${bridgeRequestId}`;
    return this.raw.delete(url);
  }
}
